import { motion } from "framer-motion";
import { Instagram, Facebook, ArrowUpRight } from "lucide-react";

const SHOPIFY_URL = "https://parfumalgerie.shop";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Accueil", href: "#hero" },
    { name: "Collection", href: "#products" },
    { name: "Notre Histoire", href: "#story" },
    { name: "Témoignages", href: "#testimonials" },
    { name: "Contact", href: "#contact" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-[#1A1A1A] text-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2"
          >
            <a
              href={SHOPIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-playfair text-2xl md:text-3xl font-medium tracking-tight inline-block mb-4"
              data-testid="footer-logo"
            >
              Parfum<span className="text-[#D4AF37]">Algérie</span>
            </a>
            <p className="font-montserrat text-sm text-white/60 leading-relaxed max-w-md mb-6">
              Votre destination de confiance pour les parfums de luxe en Algérie.
              Découvrez notre collection exclusive de fragrances authentiques
              livrées partout dans les 48 wilayas.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/parfum.algerie/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram size={18} strokeWidth={1.5} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61576175205819"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook size={18} strokeWidth={1.5} />
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="font-playfair text-lg mb-6">Navigation</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="font-montserrat text-sm text-white/60 hover:text-[#D4AF37] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Shop CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="font-playfair text-lg mb-6">Boutique</h4>
            <p className="font-montserrat text-sm text-white/60 mb-4">
              Visitez notre boutique en ligne pour découvrir toute notre
              collection.
            </p>
            <a
              href={SHOPIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-montserrat text-sm text-[#D4AF37] hover:text-white transition-colors group"
              data-testid="footer-shop-link"
            >
              Visiter la Boutique
              <ArrowUpRight
                size={14}
                className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>

            {/* Payment Info */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="font-montserrat text-xs text-white/40 uppercase tracking-wide mb-3">
                Paiement Sécurisé
              </p>
              <p className="font-montserrat text-sm text-white/60">
                Paiement à la livraison disponible
              </p>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <p className="font-montserrat text-xs text-white/40">
            © {currentYear} ParfumAlgérie. Tous droits réservés.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="#"
              className="font-montserrat text-xs text-white/40 hover:text-white/60 transition-colors"
            >
              Politique de Confidentialité
            </a>
            <a
              href="#"
              className="font-montserrat text-xs text-white/40 hover:text-white/60 transition-colors"
            >
              Conditions d'Utilisation
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
